import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { useSnackbar } from '@app/hooks/useSnackbar';
import { Loading } from '@components/ui';
import { UserRole } from '@dieterApi/user/useUserQuery';
import {
  TenantRegistrationRequest,
  useTenantRegistrationRequestsQuery,
} from '@dieterApi/tenantRegistration/useTenantRegistrationRequestsQuery';
import {
  useApproveTenantRegistrationRequestMutation,
  useRejectTenantRegistrationRequestMutation,
  useResetTenantRegistrationRequestMutation,
  useUpdateTenantRegistrationRequestMutation,
} from '@dieterApi/tenantRegistration/useTenantRegistrationMutations';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RestoreIcon from '@mui/icons-material/Restore';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useAsyncCallback } from 'react-async-hook';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

function statusChip(status: TenantRegistrationRequest['status'], t: (key: string, fallback: string) => string) {
  switch (status) {
    case 'Approved':
      return <Chip size="small" color="success" label={t('route.tenant-registrations.status.approved', 'Genehmigt')} />;
    case 'Rejected':
      return <Chip size="small" color="error" label={t('route.tenant-registrations.status.rejected', 'Abgelehnt')} />;
    default:
      return <Chip size="small" color="warning" label={t('route.tenant-registrations.status.pending', 'Offen')} />;
  }
}

interface EditForm {
  companyName: string;
  website: string;
  phone: string;
  street: string;
  zip: string;
  city: string;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
}

function toEditForm(request: TenantRegistrationRequest): EditForm {
  return {
    companyName: request.companyName,
    website: request.website ?? '',
    phone: request.phone,
    street: request.street,
    zip: request.zip,
    city: request.city,
    contactFirstName: request.contactFirstName,
    contactLastName: request.contactLastName,
    contactEmail: request.contactEmail,
  };
}

export function TenantRegistrations() {
  const { t, i18n } = useTranslation();
  const { user, activeCompany } = useUserWithCompany();
  const { enqueueSnackbar } = useSnackbar();

  const hasAccess =
    user?.role === UserRole.Admin &&
    (activeCompany?.isPrincipal ?? false) &&
    ((user?.hasFeature('MultiTenancy') ?? false) || (user?.hasFeature('DpoManaged') ?? false));

  const { data, loading } = useTenantRegistrationRequestsQuery();
  const [approve] = useApproveTenantRegistrationRequestMutation();
  const [reject] = useRejectTenantRegistrationRequestMutation();
  const [update] = useUpdateTenantRegistrationRequestMutation();
  const [reset] = useResetTenantRegistrationRequestMutation();

  const [rejectTarget, setRejectTarget] = useState<TenantRegistrationRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const [editTarget, setEditTarget] = useState<TenantRegistrationRequest | null>(null);
  const [editForm, setEditForm] = useState<EditForm | null>(null);

  const requests = data?.tenantRegistrationRequests ?? [];

  const handleApprove = useAsyncCallback(async (request: TenantRegistrationRequest) => {
    try {
      await approve({ variables: { id: request.id } });
      enqueueSnackbar(
        t('route.tenant-registrations.approve-success', 'Mandant wurde erstellt und der Nutzer eingeladen.'),
        { variant: 'success' }
      );
    } catch (err) {
      enqueueSnackbar((err as Error).message, { variant: 'error' });
    }
  });

  const handleReject = useAsyncCallback(async () => {
    if (!rejectTarget) return;
    try {
      await reject({ variables: { id: rejectTarget.id, reason: rejectReason || undefined } });
      enqueueSnackbar(t('route.tenant-registrations.reject-success', 'Anfrage wurde abgelehnt.'), {
        variant: 'success',
      });
      setRejectTarget(null);
      setRejectReason('');
    } catch (err) {
      enqueueSnackbar((err as Error).message, { variant: 'error' });
    }
  });

  const openEdit = (request: TenantRegistrationRequest) => {
    setEditTarget(request);
    setEditForm(toEditForm(request));
  };

  const closeEdit = () => {
    setEditTarget(null);
    setEditForm(null);
  };

  const handleUpdate = useAsyncCallback(async () => {
    if (!editTarget || !editForm) return;
    try {
      await update({
        variables: {
          id: editTarget.id,
          companyName: editForm.companyName,
          website: editForm.website || null,
          phone: editForm.phone,
          street: editForm.street,
          zip: editForm.zip,
          city: editForm.city,
          contactFirstName: editForm.contactFirstName,
          contactLastName: editForm.contactLastName,
          contactEmail: editForm.contactEmail,
        },
      });
      enqueueSnackbar(t('route.tenant-registrations.edit-success', 'Anfrage wurde aktualisiert.'), {
        variant: 'success',
      });
      closeEdit();
    } catch (err) {
      enqueueSnackbar((err as Error).message, { variant: 'error' });
    }
  });

  const handleReset = useAsyncCallback(async (request: TenantRegistrationRequest) => {
    try {
      await reset({ variables: { id: request.id } });
      enqueueSnackbar(
        t('route.tenant-registrations.reset-success', 'Anfrage wurde auf den Originalzustand zurückgesetzt.'),
        { variant: 'success' }
      );
    } catch (err) {
      enqueueSnackbar((err as Error).message, { variant: 'error' });
    }
  });

  return (
    <div className="2xl:container flex flex-col p-6">
      <h1>{t('route.tenant-registrations.header', 'Registrierungsanfragen')}</h1>
      <p>
        {t(
          'route.tenant-registrations.description',
          'Prüfen Sie eingehende Registrierungsanfragen von Partnerfirmen und legen Sie diese als Untermandanten an.'
        )}
      </p>

      <div className="min-h-[40px] flex justify-end">
        <div>
          <Link to="/dashboard">{t('common.action.back', 'Zurück')}</Link>
        </div>
      </div>

      {!hasAccess ? (
        <p className="text-lg text-red-600">
          {t('route.tenant-registrations.no-permission', 'Sie haben keine Berechtigung für diese Seite.')}
        </p>
      ) : loading && requests.length === 0 ? (
        <Loading />
      ) : requests.length === 0 ? (
        <p className="text-gray-500 mt-4">
          {t('route.tenant-registrations.empty', 'Es liegen derzeit keine Registrierungsanfragen vor.')}
        </p>
      ) : (
        <TableContainer className="mt-4">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('route.tenant-registrations.col.date', 'Datum')}</TableCell>
                <TableCell>{t('route.tenant-registrations.col.company', 'Firma')}</TableCell>
                <TableCell>{t('route.tenant-registrations.col.contact', 'Kontakt')}</TableCell>
                <TableCell>{t('route.tenant-registrations.col.address', 'Adresse')}</TableCell>
                <TableCell>{t('route.tenant-registrations.col.status', 'Status')}</TableCell>
                <TableCell align="right">{t('route.tenant-registrations.col.actions', 'Aktionen')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request.id} hover>
                  <TableCell>
                    {DateTime.fromISO(request.createdAt).setLocale(i18n.language).toLocaleString(DateTime.DATE_MED)}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{request.companyName}</div>
                    {request.website && <div className="text-xs text-gray-500">{request.website}</div>}
                    <div className="text-xs text-gray-500">{request.phone}</div>
                  </TableCell>
                  <TableCell>
                    <div>
                      {request.contactFirstName} {request.contactLastName}
                    </div>
                    <div className="text-xs text-gray-500">{request.contactEmail}</div>
                  </TableCell>
                  <TableCell>
                    <div>{request.street}</div>
                    <div className="text-xs text-gray-500">
                      {request.zip} {request.city}
                    </div>
                  </TableCell>
                  <TableCell>
                    {statusChip(request.status, t)}
                    {request.isEdited && (
                      <div className="mt-1">
                        <Chip
                          size="small"
                          variant="outlined"
                          color="info"
                          label={t('route.tenant-registrations.edited', 'Bearbeitet')}
                        />
                      </div>
                    )}
                    {request.status === 'Rejected' && request.rejectionReason && (
                      <div className="text-xs text-gray-500 mt-1">{request.rejectionReason}</div>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {request.status === 'Pending' && (
                      <div className="flex gap-2 justify-end">
                        <Tooltip title={t('route.tenant-registrations.approve', 'Genehmigen')}>
                          <span>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckIcon />}
                              disabled={handleApprove.loading}
                              onClick={() => handleApprove.execute(request)}
                            >
                              {t('route.tenant-registrations.approve', 'Genehmigen')}
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title={t('route.tenant-registrations.edit', 'Bearbeiten')}>
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditOutlinedIcon />}
                              onClick={() => openEdit(request)}
                            >
                              {t('route.tenant-registrations.edit', 'Bearbeiten')}
                            </Button>
                          </span>
                        </Tooltip>
                        {request.isEdited && (
                          <Tooltip title={t('route.tenant-registrations.reset', 'Zurücksetzen')}>
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                color="info"
                                startIcon={<RestoreIcon />}
                                disabled={handleReset.loading}
                                onClick={() => handleReset.execute(request)}
                              >
                                {t('route.tenant-registrations.reset', 'Zurücksetzen')}
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip title={t('route.tenant-registrations.reject', 'Ablehnen')}>
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<CloseIcon />}
                              onClick={() => setRejectTarget(request)}
                            >
                              {t('route.tenant-registrations.reject', 'Ablehnen')}
                            </Button>
                          </span>
                        </Tooltip>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!rejectTarget} onClose={() => setRejectTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t('route.tenant-registrations.reject-title', 'Anfrage ablehnen')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              'route.tenant-registrations.reject-desc',
              'Möchten Sie diese Registrierungsanfrage ablehnen? Sie können optional einen Grund angeben.'
            )}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            multiline
            minRows={2}
            label={t('route.tenant-registrations.reject-reason', 'Grund (optional)')}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>{t('common.action.cancel', 'Abbrechen')}</Button>
          <Button color="error" disabled={handleReject.loading} onClick={() => handleReject.execute()}>
            {t('route.tenant-registrations.reject', 'Ablehnen')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editTarget} onClose={closeEdit} fullWidth maxWidth="sm">
        <DialogTitle>{t('route.tenant-registrations.edit-title', 'Anfrage bearbeiten')}</DialogTitle>
        <DialogContent>
          {editForm && (
            <div className="flex flex-col gap-3 mt-1">
              <TextField
                label={t('route.tenant-registrations.field.company', 'Firma')}
                value={editForm.companyName}
                onChange={(e) => setEditForm({ ...editForm, companyName: e.target.value })}
                fullWidth
              />
              <TextField
                label={t('route.tenant-registrations.field.website', 'Webseite')}
                value={editForm.website}
                onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                fullWidth
              />
              <TextField
                label={t('route.tenant-registrations.field.phone', 'Telefon')}
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                fullWidth
              />
              <TextField
                label={t('route.tenant-registrations.field.street', 'Straße')}
                value={editForm.street}
                onChange={(e) => setEditForm({ ...editForm, street: e.target.value })}
                fullWidth
              />
              <div className="flex gap-3">
                <TextField
                  label={t('route.tenant-registrations.field.zip', 'PLZ')}
                  value={editForm.zip}
                  onChange={(e) => setEditForm({ ...editForm, zip: e.target.value })}
                  className="w-1/3"
                />
                <TextField
                  label={t('route.tenant-registrations.field.city', 'Ort')}
                  value={editForm.city}
                  onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                  className="w-2/3"
                />
              </div>
              <div className="flex gap-3">
                <TextField
                  label={t('route.tenant-registrations.field.first-name', 'Vorname')}
                  value={editForm.contactFirstName}
                  onChange={(e) => setEditForm({ ...editForm, contactFirstName: e.target.value })}
                  fullWidth
                />
                <TextField
                  label={t('route.tenant-registrations.field.last-name', 'Nachname')}
                  value={editForm.contactLastName}
                  onChange={(e) => setEditForm({ ...editForm, contactLastName: e.target.value })}
                  fullWidth
                />
              </div>
              <TextField
                label={t('route.tenant-registrations.field.email', 'E-Mail')}
                value={editForm.contactEmail}
                onChange={(e) => setEditForm({ ...editForm, contactEmail: e.target.value })}
                fullWidth
              />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit}>{t('common.action.cancel', 'Abbrechen')}</Button>
          <Button variant="contained" disabled={handleUpdate.loading} onClick={() => handleUpdate.execute()}>
            {t('common.action.save', 'Speichern')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
